import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getVenueLocationById, deleteVenueLocation } from '../utils/api';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ChevronLeft, Edit2, Trash2, AlertCircle, Loader, MapPin } from 'lucide-react';

const STATUS_COLORS = {
  pending: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-900', badge: 'bg-yellow-100 text-yellow-800' },
  approved: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-900', badge: 'bg-green-100 text-green-800' },
  rejected: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-900', badge: 'bg-red-100 text-red-800' },
  active: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-900', badge: 'bg-blue-100 text-blue-800' }
};

export default function ViewVenueLocationChangeRequest() {
  const navigate = useNavigate();
  const { id } = useParams();
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);

  const [venueLocation, setVenueLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Initialize Leaflet Map
  const initializeMap = (lat, lng) => {
    if (mapInstanceRef.current) return; // Already initialized
    
    if (!mapRef.current) return;

    try {
      // Create map
      mapInstanceRef.current = L.map(mapRef.current).setView([parseFloat(lat), parseFloat(lng)], 15);

      // Add OpenStreetMap tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
      }).addTo(mapInstanceRef.current);

      // Add marker
      markerRef.current = L.marker([parseFloat(lat), parseFloat(lng)])
        .bindPopup(venueLocation?.venueName || 'Venue Location')
        .addTo(mapInstanceRef.current);
    } catch (error) {
      console.error('Error initializing map:', error);
    }
  };

  useEffect(() => {
    const fetchVenueLocation = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await getVenueLocationById(id);
        setVenueLocation(response.data);

        // Initialize map with coordinates
        setTimeout(() => {
          initializeMap(response.data.latitude, response.data.longitude);
        }, 100);
      } catch (err) {
        console.error('Error fetching venue location:', err);
        setError('Failed to load venue location details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchVenueLocation();
    }

    // Cleanup on unmount
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [id]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteVenueLocation(id);
      setDeleteConfirm(false);
      navigate('/venue-location-change-requests', { state: { message: 'Venue location deleted successfully' } });
    } catch (err) {
      console.error('Error deleting venue location:', err);
      setError('Failed to delete venue location');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeColor = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.pending;
  };

  const canEdit = (status) => {
    return status === 'pending' || status === 'rejected';
  };

  const canDelete = (status) => {
    return status === 'pending';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6 flex items-center justify-center">
        <div className="text-center">
          <Loader className="animate-spin mx-auto mb-4 text-blue-600" size={48} />
          <p className="mt-4 text-gray-600">Loading venue location...</p>
        </div>
      </div>
    );
  }

  if (error || !venueLocation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/venue-location-change-requests')}
            className="mb-6 flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition"
          >
            <ChevronLeft size={18} />
            Back to Requests
          </button>
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <AlertCircle className="mx-auto mb-4 text-red-600" size={48} />
            <p className="text-red-800 font-semibold">Error: {error || 'Venue location not found'}</p>
          </div>
        </div>
      </div>
    );
  }

  const colors = getStatusBadgeColor(venueLocation.status);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <button
            onClick={() => navigate('/venue-location-change-requests')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition w-fit"
          >
            <ChevronLeft size={18} />
            Back to Requests
          </button>
          <div className="flex flex-col sm:flex-row gap-2">
            {canEdit(venueLocation.status) && (
              <button
                onClick={() => navigate(`/register-venue-location?edit=${id}`)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition"
              >
                <Edit2 size={18} />
                Edit
              </button>
            )}
            {canDelete(venueLocation.status) && (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                <Trash2 size={18} />
                Delete
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex gap-3 items-start">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-800 font-medium">{error}</p>
            </div>
          </div>
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="text-red-800 font-medium">Are you sure you want to delete this venue location?</p>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:bg-gray-400"
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Header */}
        <div className={`${colors.bg} border ${colors.border} rounded-xl p-4 md:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{venueLocation.venueName}</h1>
              <p className="text-sm text-gray-600 mt-2">Venue Code: {venueLocation.venueCode}</p>
            </div>
            <span className={`px-4 py-2 rounded-full text-base md:text-lg font-semibold w-fit ${colors.badge}`}>
              {venueLocation.status.toUpperCase()}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Manager Information */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Manager Information</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-gray-600">Full Name</p>
                  <p className="text-base md:text-lg font-medium text-gray-900">{venueLocation.fullName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Mobile Number</p>
                  <p className="text-base md:text-lg font-medium text-gray-900">{venueLocation.mobile}</p>
                </div>
              </div>
            </div>

            {/* Location Information */}
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Location Information</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600">Latitude</p>
                    <p className="text-base md:text-lg font-medium text-gray-900 font-mono">{venueLocation.latitude.toFixed(6)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Longitude</p>
                    <p className="text-base md:text-lg font-medium text-gray-900 font-mono">{venueLocation.longitude.toFixed(6)}</p>
                  </div>
                </div>
                {venueLocation.address && (
                  <div>
                    <p className="text-sm text-gray-600">Address</p>
                    <p className="text-gray-900">{venueLocation.address}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Status History */}
            {venueLocation.statusHistory && venueLocation.statusHistory.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Status History</h2>
                <div className="space-y-3">
                  {venueLocation.statusHistory.map((history, idx) => {
                    const byName = history.status === 'pending'
                      ? venueLocation.fullName
                      : 'Admin';
                    const actionLabel = history.status === 'pending'
                      ? `Submitted by ${byName}`
                      : history.status === 'approved'
                      ? `Approved by ${byName}`
                      : history.status === 'rejected'
                      ? `Rejected by ${byName}`
                      : `${history.status} by ${byName}`;
                    return (
                      <div
                        key={idx}
                        className={`rounded-lg p-4 border-l-4 ${
                          history.status === 'approved'
                            ? 'border-green-500 bg-green-50'
                            : history.status === 'rejected'
                            ? 'border-red-500 bg-red-50'
                            : 'border-yellow-500 bg-yellow-50'
                        }`}
                      >
                        <div className="flex flex-col gap-1">
                          <p className="font-semibold text-gray-900 text-base">{actionLabel}</p>
                          <p className="text-sm text-gray-600">{formatDate(history.changedAt)}</p>
                          {history.remarks && history.remarks !== 'Initial submission' && history.remarks !== 'Approved' && (
                            <p className="text-sm text-gray-700 italic border-t pt-2 mt-1">"{history.remarks}"</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Map Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100 sticky top-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <MapPin size={24} className="text-blue-700" strokeWidth={2.5} />
                </div>
                <h2 className="text-lg font-semibold text-gray-900">Location Map</h2>
              </div>
              <div
                ref={mapRef}
                className="w-full rounded-lg border-2 border-blue-200"
                style={{ minHeight: '320px' }}
              ></div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">Coordinates</p>
                <p className="text-xs text-gray-600 mt-2 font-mono break-all">
                  Lat: {venueLocation.latitude.toFixed(6)}
                </p>
                <p className="text-xs text-gray-600 mt-1 font-mono break-all">
                  Long: {venueLocation.longitude.toFixed(6)}
                </p>
                <a
                  href={`https://maps.google.com/?q=${venueLocation.latitude},${venueLocation.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 block w-full text-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
                >
                  Open in Google Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
